import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function detectPlatform() {
  const ua = navigator.userAgent;
  const platform = navigator.platform || '';
  
  const isWindows = /Win/i.test(platform) || /Windows/i.test(ua);
  const isMac = /Mac/i.test(platform);
  const isLinux = /Linux/i.test(ua) && !/Android/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isChromeOS = /CrOS/i.test(ua);
  
  const isChrome = /Chrome/i.test(ua) && !/Edg/i.test(ua) && !/OPR/i.test(ua);
  const isEdge = /Edg/i.test(ua);
  const isFirefox = /Firefox/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome/i.test(ua);
  
  let os = 'Unknown';
  if (isWindows) os = 'Windows';
  else if (isMac) os = 'macOS';
  else if (isAndroid) os = 'Android';
  else if (isIOS) os = 'iOS';
  else if (isChromeOS) os = 'Chrome OS';
  else if (isLinux) os = 'Linux';
  
  let browser = 'Unknown';
  if (isChrome) browser = 'Chrome';
  else if (isEdge) browser = 'Edge';
  else if (isFirefox) browser = 'Firefox';
  else if (isSafari) browser = 'Safari';
  
  return { os, browser, isWindows, isMac, isLinux, isAndroid, isIOS, isChrome, isEdge, isFirefox, isSafari };
}

export default function InstallPage() {
  const [platform, setPlatform] = useState(null);
  const [versions, setVersions] = useState({});
  const [showInstructions, setShowInstructions] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    setPlatform(detectPlatform());
    axios.get(`${API}/admin/updates/versions`).then(r => setVersions(r.data.versions || {})).catch(() => {});
  }, []);

  const canInstallExtension = platform?.isChrome || platform?.isEdge;
  const canInstallDesktop = platform?.isWindows;
  const canInstallMobile = platform?.isAndroid || platform?.isIOS;

  const installOptions = [
    {
      id: 'extension',
      title: 'Chrome / Edge Extension',
      versionKey: 'Chrome / Edge Extension',
      subtitle: canInstallExtension ? 'Available for your browser' : 'Requires Chrome or Edge browser',
      available: canInstallExtension,
      color: '#4ade80',
      icons: ['/chrome-icon.png', '/edge-icon.png'],
      features: [
        'PiP any tab (beta), website, or video',
        'Full mouse, keyboard & touch interaction in PiP',
        'Favorites with scheduled PiP on/off times',
        'Custom frames & RGB backlights',
        'Theatre mode for immersive viewing',
        'Real-time opacity & size controls',
        'Multiple simultaneous PiP windows',
        'Audio override controls',
      ],
      trialAction: { label: 'Install Free Trial', href: '/downloads' },
      buyAction: { label: 'Buy Lifetime License — $39', href: '/pricing' },
    },
    {
      id: 'desktop',
      title: 'Windows 11 Desktop App',
      versionKey: 'Windows 11 Desktop App',
      subtitle: canInstallDesktop ? 'Available for your system' : 'Requires Windows 11',
      available: canInstallDesktop,
      color: '#60a5fa',
      icons: ['/windows-icon.png'],
      features: [
        'True Always-On-Top PiP windows',
        'System tray integration',
        'Ctrl+Shift+P global shortcut',
        'Custom frame styles & theatre mode',
        'Timer & alarm pop-ups',
        'Team collaboration support',
      ],
      trialAction: { label: 'Install Free Trial', href: '/downloads' },
      buyAction: { label: 'Buy Lifetime License — $39', href: '/pricing' },
    },
    {
      id: 'mobile',
      title: 'Mobile App (iOS + Android)',
      versionKey: 'iOS',
      subtitle: canInstallMobile ? `Available for your ${platform?.os} device` : 'Available on iOS & Android',
      available: canInstallMobile,
      color: '#f472b6',
      icons: ['/ios-icon.png', '/android-icon.png'],
      features: [
        'WebView PiP with full navigation',
        'Native PiP mode on Android',
        'Custom frames & themes',
        'Secure credential storage',
        'Audio controls',
        'Timer & alarm functionality',
      ],
      trialAction: { label: 'Install Free Trial', href: '/downloads' },
      buyAction: { label: 'Buy Lifetime License — $39', href: '/pricing' },
    },
  ];

  return (
    <Layout>
      <div className="min-h-[calc(100vh-8rem)] py-16 px-4" data-testid="install-page">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4 neon-text-white" data-testid="install-title">
              Install Top Screen&#8482;
            </h1>
            <p className="text-[#E8E4DC] text-lg max-w-2xl mx-auto">
              <span className="neon-white-text block">Get the best experience for your device.</span>
              <span className="neon-white-text block">All versions include the same powerful features.</span>
            </p>
          </div>

          {/* Detected Platform Badge */}
          {platform && (
            <div className="flex justify-center mb-12" data-testid="platform-detection">
              <div className="card-glass px-6 py-3 inline-flex items-center gap-3 rounded-full">
                <div className="w-2 h-2 rounded-full bg-[#A3E635] animate-pulse"></div>
                <span className="text-[#E8E4DC] text-sm">
                  Detected: <span className="text-white font-semibold">{platform.os}</span>
                  {platform.browser !== 'Unknown' && (
                    <> running <span className="text-white font-semibold">{platform.browser}</span></>
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Install Options — recommended first */}
          <div className="grid gap-8 lg:grid-cols-3">
            {installOptions
              .sort((a, b) => (b.available ? 1 : 0) - (a.available ? 1 : 0))
              .map(opt => (
              <div
                key={opt.id}
                className={`relative group ${opt.available ? '' : 'opacity-60'}`}
                data-testid={`install-option-${opt.id}`}
              >
                {opt.available && (
                  <div className="absolute -top-3 left-4 z-10">
                    <span className="bg-[#A3E635] text-black text-xs font-bold px-3 py-1 rounded-full">
                      Recommended
                    </span>
                  </div>
                )}
                <div
                  className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"
                  style={{ background: `linear-gradient(135deg, ${opt.color}33, transparent)` }}
                />
                <div className={`relative card-glass rounded-2xl p-6 h-full flex flex-col transition-all duration-300 ${opt.available ? 'border-2' : 'border border-white/5'}`} style={opt.available ? { borderColor: `${opt.color}60` } : {}}>
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center gap-1 mb-4" style={{ background: `${opt.color}15` }}>
                    {opt.icons.map((ico, ii) => (
                      <img key={ii} src={ico} alt="" className="w-7 h-7 object-contain" />
                    ))}
                  </div>

                  <h2 className="font-heading text-xl font-bold text-white mb-1 flex items-center gap-2 flex-wrap">
                    <span>{opt.title}</span>
                    {versions[opt.versionKey] && (
                      <span className="text-xs font-mono px-2 py-0.5 rounded-md" style={{ background: `${opt.color}18`, color: opt.color }} data-testid={`install-version-${opt.id}`}>
                        v{versions[opt.versionKey]}
                      </span>
                    )}
                  </h2>
                  <p className="text-sm mb-5" style={{ color: opt.available ? opt.color : '#6b7280' }}>{opt.subtitle}</p>

                  {/* Features */}
                  <ul className="flex-1 space-y-2.5 mb-6">
                    {opt.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#E8E4DC]">
                        <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: opt.color }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    {user ? (
                      <button
                        onClick={() => setShowInstructions(opt.id)}
                        className="w-full py-3 px-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                        style={{ background: `${opt.color}18`, border: `1px solid ${opt.color}40`, color: opt.color }}
                        data-testid={`install-trial-${opt.id}`}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        {opt.trialAction.label}
                      </button>
                    ) : (
                      <Link
                        to="/downloads"
                        className="w-full py-3 px-4 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
                        style={{ background: `${opt.color}18`, border: `1px solid ${opt.color}40`, color: opt.color }}
                        data-testid={`install-trial-${opt.id}`}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        {opt.trialAction.label}
                      </Link>
                    )}
                    <Link
                      to={opt.buyAction.href}
                      className="w-full py-3 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] bg-[#A3E635] text-black hover:bg-[#8CD025]"
                      data-testid={`install-buy-${opt.id}`}
                    >
                      {opt.buyAction.label}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Feature Highlights */}
          <div className="mt-20">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl font-bold text-white mb-3">Every Version Includes</h2>
              <p className="text-[#E8E4DC]">No matter which platform you choose, you get the full Top Screen&#8482; experience.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { title: "Custom Frames", desc: "Classic frames, RGB backlights, neon borders", color: "#A3E635" },
                { title: "Theatre Mode", desc: "Immersive cinematic viewing with dimmed surroundings", color: "#60a5fa" },
                { title: "Multiple Screens", desc: "Run several PiP windows at once", color: "#f472b6" },
                { title: "Audio Override", desc: "Independent volume & mute controls", color: "#facc15" },
                { title: "Timer & Alarms", desc: "Schedule content pop-ups at specific times", color: "#4ade80" },
                { title: "Team Collaboration", desc: "Share screens across your team", color: "#c084fc" },
              ].map((f, i) => (
                <div key={i} className="card-glass p-4 rounded-xl" data-testid={`install-feature-${i}`}>
                  <div className="w-2 h-2 rounded-full mb-3" style={{ background: f.color }}></div>
                  <h3 className="text-white font-semibold text-sm mb-1">{f.title}</h3>
                  <p className="text-[#E8E4DC] text-xs">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Trial Info */}
          <div className="mt-16 text-center">
            <div className="card-glass p-6 max-w-xl mx-auto rounded-2xl">
              <h3 className="text-white font-semibold mb-3">72-Hour Full-Feature Trial</h3>
              <p className="text-[#E8E4DC] text-sm leading-relaxed mb-4">
                <span className="block">No credit card required.</span>
                <span className="block">Install starts with a 72-hour trial of all premium features.</span>
                <span className="block">When you're ready, upgrade to a lifetime license</span>
                <span className="block">for just <span className="text-[#A3E635] font-bold">$39</span> — one payment, forever.</span>
              </p>
              <Link to="/register" className="btn-primary inline-block" data-testid="install-start-trial">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Install Instructions Modal (signed in users) */}
      {showInstructions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.8)' }} onClick={() => setShowInstructions(false)}>
          <div className="card-glass rounded-2xl p-8 max-w-lg w-full" style={{ background: 'rgba(10,10,20,0.98)' }} onClick={e => e.stopPropagation()} data-testid="install-instructions-modal">
            <h2 className="font-heading text-2xl font-bold text-white mb-4">Install Instructions</h2>
            {showInstructions === 'extension' && (
              <div className="space-y-3 text-[#E8E4DC] text-sm">
                <p>1. Go to the <Link to="/downloads" className="text-[#A3E635] underline">Downloads</Link> page and download the Chrome Extension ZIP.</p>
                <p>2. Extract the ZIP file to a folder on your computer.</p>
                <p>3. Open Chrome and go to <span className="text-white font-mono text-xs bg-white/10 px-2 py-0.5 rounded">chrome://extensions</span></p>
                <p>4. Enable <span className="text-white">"Developer mode"</span> in the top-right corner.</p>
                <p>5. Click <span className="text-white">"Load unpacked"</span> and select the extracted folder.</p>
                <p>6. The Top Screen&#8482; icon will appear in your toolbar. Click it to start!</p>
              </div>
            )}
            {showInstructions === 'desktop' && (
              <div className="space-y-3 text-[#E8E4DC] text-sm">
                <p>1. Go to the <Link to="/downloads" className="text-[#A3E635] underline">Downloads</Link> page and download the Windows 11 Desktop App.</p>
                <p>2. Run the installer and follow the setup wizard.</p>
                <p>3. Launch Top Screen&#8482; from your Start menu or desktop shortcut.</p>
                <p>4. Use <span className="text-white font-mono text-xs bg-white/10 px-2 py-0.5 rounded">Ctrl+Shift+P</span> to toggle PiP mode from anywhere.</p>
              </div>
            )}
            {showInstructions === 'mobile' && (
              <div className="space-y-3 text-[#E8E4DC] text-sm">
                <p>1. Go to the <Link to="/downloads" className="text-[#A3E635] underline">Downloads</Link> page and download the app for your device.</p>
                <p>2. Install the app on your iOS or Android device.</p>
                <p>3. Sign in with your account to sync your subscription.</p>
                <p>4. Use the floating PiP button to start pinning content.</p>
              </div>
            )}
            <button onClick={() => setShowInstructions(false)} className="btn-primary mt-6 w-full" data-testid="close-instructions">Got It</button>
          </div>
        </div>
      )}
    </Layout>
  );
}
